import { apiService } from "./api";
import { Product } from "../types";

// Campaigns — see api/app/services/campaigns.py for the pricing rules:
// each product gets its own campaign price (blank = badge only), kept beside
// the product's own sale; customers get whichever is lower, and when the
// campaign ends its own sale carries on.

export type DiscountType = "none" | "percent" | "flat";
export type CampaignStatus = "scheduled" | "live" | "ended";
/**
 * Whose campaign price the product carries:
 *   campaign       — this campaign's
 *   other_campaign — another scheduled/live campaign's (locked here)
 *   none           — none
 */
export type PriceState = "campaign" | "other_campaign" | "none";

export interface CampaignSummary {
    campaign_id: string;
    name: string;
    slug: string;
    badge_label: string;
    banner_image_url: string | null;
    description?: string | null;
    starts_at: string;
    ends_at: string | null;
    show_on_home: boolean;
    discount_type: DiscountType;
    discount_value: number | null;
    status: CampaignStatus;
    product_count: number;
    max_discount_percent: number | null;
    create_time: string;
    update_time: string;
}

export interface CampaignItem {
    product: Product;
    position: number;
    campaign_price: number | null;
    price_state: PriceState;
    other_campaign_name: string | null;
    /** The product's own sale when on or still to come — it stays beside
     * the campaign price, and customers get the lower one. */
    own_sale_price: number | null;
}

export interface CampaignDetail extends CampaignSummary {
    description: string | null;
    items: CampaignItem[];
    created_by_user_id?: string | null;
    updated_by_user_id?: string | null;
}

export interface CampaignSave {
    name: string;
    slug?: string;
    badge_label?: string;
    description?: string | null;
    starts_at: string;
    ends_at: string | null;
    show_on_home: boolean;
    discount_type: DiscountType;
    discount_value: number | null;
    /** In page order. A null price = no campaign price for that product. */
    products?: { product_id: string; campaign_price: number | null }[];
}

export interface AddProductsResult {
    added: number;
    without_discount: { product_id: string; name: string; reason: string }[];
    skipped: { product_id: string; name: string; reason: string }[];
}

/** The campaign price for one product, in whole taka; null when the
 * discount wouldn't give a real price (same rule as the API). */
export function discountedPrice(
    sellingPrice: number,
    type: DiscountType,
    value: number | null | undefined,
): number | null {
    if (type === "none" || !value || value <= 0) return null;
    const raw = type === "percent" ? (sellingPrice * (100 - value)) / 100 : sellingPrice - value;
    const price = Math.round(raw);
    if (price <= 0 || price >= sellingPrice) return null;
    return price;
}

class CampaignService {
    list(): Promise<CampaignSummary[]> {
        return apiService.get<CampaignSummary[]>("/campaigns");
    }

    get(id: string): Promise<CampaignDetail> {
        return apiService.get<CampaignDetail>(`/campaigns/${id}`);
    }

    create(body: CampaignSave): Promise<CampaignDetail> {
        return apiService.post<CampaignDetail>("/campaigns", body);
    }

    update(id: string, body: CampaignSave): Promise<CampaignDetail> {
        return apiService.put<CampaignDetail>(`/campaigns/${id}`, body);
    }

    endNow(id: string): Promise<CampaignDetail> {
        return apiService.post<CampaignDetail>(`/campaigns/${id}/end`, {});
    }

    remove(id: string): Promise<void> {
        return apiService.delete<void>(`/campaigns/${id}`);
    }

    addProducts(id: string, productIds: string[]): Promise<AddProductsResult> {
        return apiService.post<AddProductsResult>(`/campaigns/${id}/products`, {
            product_ids: productIds,
        });
    }

    uploadBanner(id: string, file: File): Promise<CampaignDetail> {
        const formData = new FormData();
        formData.append("file", file);
        return apiService.post<CampaignDetail>(`/campaigns/${id}/banner`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    }

    deleteBanner(id: string): Promise<CampaignDetail> {
        return apiService.delete<CampaignDetail>(`/campaigns/${id}/banner`);
    }
}

export const campaignService = new CampaignService();
